import { Suspense, lazy, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { api } from "./lib/api";
import LoginPage from "./pages/Login";
import DealsPage from "./pages/Deals";
import DealPage from "./pages/Deal";
import ReviewPage from "./pages/Review";
import SharePage from "./pages/Share";
import AdminConventionsPage from "./pages/AdminConventions";
import RepositoryLibraryPage from "./pages/RepositoryLibrary";
import RepositoryDealPage from "./pages/RepositoryDeal";
import RepositoryAskPage from "./pages/RepositoryAsk";
import RedlineCreatorPage from "./pages/RedlineCreator";
// Lazy — pulls in pdf.js (~1 MB); only load it when a PDF is actually opened.
const RepositoryPdfPage = lazy(() => import("./pages/RepositoryPdf"));
import Layout from "./components/Layout";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading, domainOk, emailVerified } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!user || !domainOk) return <Navigate to="/login" state={{ from: loc }} replace />;
  // The backend rejects every /api call from an unverified user (requireAuth
  // requires email_verified). Without this gate such a user lands on the app
  // shell and every request 403s silently — instead, send them to /login,
  // which renders the "Verify your email" screen. Google Workspace users are
  // auto-verified; this only stops unverified email/password (outside-counsel)
  // sign-ins.
  if (!emailVerified) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Admin-only routes (Repository, Conventions). isAdmin is null until the
 * /api/me lookup resolves — show the loading state then, so admins following
 * a deep link aren't bounced to "/" before their status is known. The backend
 * enforces the same gate on every API call; this guard is just UX.
 */
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth();
  if (isAdmin === null) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  // /api/me is pinged by AuthProvider on every login (provisions the user
  // record and resolves admin status).
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/share/:token" element={<SharePage />} />
      {/* Full-tab PDF viewer (no app chrome), opened in a new tab. */}
      <Route
        path="/repository/pdf/:docId"
        element={
          <RequireAuth>
            <RequireAdmin>
              <Suspense fallback={<div className="p-8 text-slate-500 text-sm">Loading viewer…</div>}>
                <RepositoryPdfPage />
              </Suspense>
            </RequireAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/"
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<DealsPage />} />
        <Route path="deals/:id" element={<DealPage />} />
        <Route path="deals/:dealId/reviews/:reviewId" element={<ReviewPage />} />
        <Route path="redline-creator" element={<RedlineCreatorPage />} />
        <Route path="repository" element={<RequireAdmin><RepositoryLibraryPage /></RequireAdmin>} />
        <Route path="repository/ask" element={<RequireAdmin><RepositoryAskPage /></RequireAdmin>} />
        <Route
          path="repository/deals/:dealKey"
          element={<RequireAdmin><RepositoryDealPage /></RequireAdmin>}
        />
        <Route path="admin/conventions" element={<RequireAdmin><AdminConventionsPage /></RequireAdmin>} />
      </Route>
    </Routes>
  );
}
